/**
 * SplitTicketModal - move a misthreaded conversation into its own ticket.
 *
 * Opened from "Split from here" on a customer email. That email becomes
 * the new ticket's description; the agent ticks everything else that
 * belongs with it. The server pre-ticks the email's reply chain (every
 * later email whose In-Reply-To / References point into it). Internal
 * notes and drafts start unticked: which notes belong to which topic is
 * the agent's call, not something the headers can tell us.
 *
 * Why this exists: a customer who starts a new topic by hitting Reply
 * All on an old email keeps that email's thread headers, so the new
 * topic threads onto the old ticket (IP2 / RM8, 2026-09-30).
 */
import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";

import { Alert, Button, Modal, Spinner } from "@/components/ui";
import { useSplitSuggestion, useSplitTicket } from "@/hooks/useSplitTicket";
import type { CommentResponse, TicketResponse } from "@/types/api";

export interface SplitTicketModalProps {
  /** The customer email the split starts at; null while closed. */
  startCommentId: string | null;
  onClose: () => void;
  ticket: TicketResponse;
  comments: CommentResponse[];
  /** Called with the new ticket's public code after a successful split. */
  onSplit: (newPublicCode: string) => void;
}

function errorDetail(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string" && detail) return detail;
  }
  return fallback;
}

function kindLabel(c: CommentResponse): string {
  if (c.is_draft) return "Draft reply";
  if (c.is_private) return "Internal note";
  if (c.author_kind === "customer") return "Email";
  return "Reply";
}

function snippet(c: CommentResponse): string {
  const firstLine = (c.body_text || "")
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  return firstLine ?? "";
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function SplitTicketModal({
  startCommentId,
  onClose,
  ticket,
  comments,
  onSplit,
}: SplitTicketModalProps) {
  const isOpen = startCommentId !== null;
  const suggestion = useSplitSuggestion(ticket.ticket_number, startCommentId);
  const splitTicket = useSplitTicket();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Everything from the start email onward, oldest first. Earlier
  // comments cannot move: the start email is the new ticket's opening
  // message, so nothing can come before it.
  const candidates = useMemo(() => {
    const start = comments.find((c) => c.id === startCommentId);
    if (!start) return [];
    const t0 = new Date(start.created_at).getTime();
    return comments
      .filter((c) => new Date(c.created_at).getTime() >= t0)
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
  }, [comments, startCommentId]);

  // Seed from the server suggestion each time the dialog opens.
  useEffect(() => {
    setError(null);
    if (suggestion.data) {
      setSelected(new Set(suggestion.data.comment_ids));
      setSubject(suggestion.data.subject);
    } else {
      setSelected(new Set(startCommentId ? [startCommentId] : []));
      setSubject("");
    }
  }, [suggestion.data, startCommentId]);

  function toggle(id: string) {
    if (id === startCommentId) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  // Closing mid-request would drop the outcome on the floor.
  function safeClose() {
    if (!splitTicket.isPending) onClose();
  }

  async function confirm() {
    setError(null);
    try {
      const result = await splitTicket.mutateAsync({
        ticketNumber: ticket.ticket_number,
        commentIds: candidates.filter((c) => selected.has(c.id)).map((c) => c.id),
        subject: subject.trim(),
      });
      onSplit(result.new_ticket.public_code);
    } catch (err) {
      setError(
        errorDetail(
          err,
          "The split may not have completed - refresh and check the ticket before retrying.",
        ),
      );
    }
  }

  const count = candidates.filter((c) => selected.has(c.id)).length;

  return (
    <Modal isOpen={isOpen} onClose={safeClose} title="Split into a new ticket" size="lg">
      <p className="text-sm text-neutral-700">
        The first email below becomes the new ticket's description. Tick
        everything else that belongs with it; it moves with its files and
        history. Later replies to the moved emails go to the new ticket.
        Nobody is emailed.
      </p>

      <div className="mt-4">
        <label
          htmlFor="split-subject"
          className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500"
        >
          New ticket subject
        </label>
        <input
          id="split-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={500}
          autoComplete="off"
          data-testid="split-subject"
          className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-neutral-200">
        {suggestion.isLoading ? (
          <div className="flex items-center gap-2 p-3 text-sm text-neutral-500">
            <Spinner size="sm" /> Finding the rest of this email thread...
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {candidates.map((c) => {
              const isStart = c.id === startCommentId;
              const isOn = selected.has(c.id);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => toggle(c.id)}
                    disabled={isStart}
                    aria-pressed={isOn}
                    data-testid={`split-row-${c.id}`}
                    className={`flex w-full items-start gap-3 px-3 py-2 text-left text-sm ${
                      isOn ? "bg-primary-50" : "hover:bg-neutral-50"
                    } ${isStart ? "cursor-default" : ""}`}
                  >
                    <i
                      className={`fa-duotone fa-thin mt-0.5 ${
                        isOn ? "fa-square-check" : "fa-square"
                      } text-neutral-500`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                        <span className="font-medium text-neutral-800">
                          {c.author_name || c.author_email}
                        </span>
                        <span>{kindLabel(c)}</span>
                        <span>{fmt(c.created_at)}</span>
                        {isStart && (
                          <span className="font-medium text-primary-700">
                            Becomes the description
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-neutral-800">
                        {snippet(c)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {suggestion.isError && (
        <Alert variant="error" className="mt-3">
          {errorDetail(suggestion.error, "Could not load this email thread.")}
        </Alert>
      )}
      {error && (
        <Alert variant="error" className="mt-3">
          {error}
        </Alert>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={safeClose}
          disabled={splitTicket.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            void confirm();
          }}
          disabled={count === 0 || suggestion.isError || splitTicket.isPending}
          isLoading={splitTicket.isPending}
          data-testid="split-confirm"
        >
          {`Move ${count} message${count === 1 ? "" : "s"} to a new ticket`}
        </Button>
      </div>
    </Modal>
  );
}

export default SplitTicketModal;
